import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface SubscriptionPaymentProps {
  selectedTrade: string | null;
  providerId?: string;
  onNext: () => void;
  onBack: () => void;
}

export default function SubscriptionPayment({ selectedTrade, providerId: propProviderId, onNext, onBack }: SubscriptionPaymentProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(propProviderId || null);

  useEffect(() => {
    // If providerId is passed as prop, use it
    if (propProviderId) {
      setProviderId(propProviderId);
      return;
    }

    // Otherwise, fetch from database
    const fetchProviderId = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('provider_profiles')
          .select('id')
          .eq('user_id', session.user.id)
          .single();
        
        if (profile) {
          setProviderId(profile.id);
        }
      }
    };
    fetchProviderId();
  }, [propProviderId]);

  const handlePayment = async () => {
    if (!providerId) {
      setError('Provider profile not found. Please go back and complete the previous step.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      console.log('Creating checkout session for provider:', providerId);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-subscription-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            providerId,
            trade: selectedTrade
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Checkout error:', data);
        throw new Error(data.error || 'Failed to create checkout session');
      }

      console.log('Checkout session created:', data);

      if (data?.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to create checkout session. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 text-center border-b border-gray-200">
          <div className="inline-block bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            Save 25% when you join today
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Become a Taskbudy Provider</h2>
          <p className="text-gray-600 text-lg">Get consistent work without quoting, chasing, or wasted time.</p>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Pricing Display */}
          <div className="text-center mb-8">
            <div className="text-6xl font-bold text-gray-900 mb-2">£378</div>
            <div className="text-gray-600 text-lg">First year · inc. VAT</div>
          </div>

          {/* What You Get */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">What you get:</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">Access to real, paying jobs in your selected cities</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">Upfront-priced work — no quoting or bidding</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">Weekly payouts straight to your bank</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">Work that fits your availability</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">Support & account management</span>
              </div>
            </div>
          </div>

          {/* Approval Required Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-shield-check-line text-blue-600"></i>
              Approval required
            </h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-blue-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm">Apply once — we verify your details</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-blue-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm">You don't go live until approved</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-blue-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm">Not approved? You get a full refund</span>
              </div>
            </div>
          </div>

          {/* Renewal Information */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6 text-center">
            <p className="text-gray-700 text-sm">
              <span className="font-semibold">After your first year:</span> £35/month (£420 + VAT per year)
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handlePayment}
            disabled={loading || !providerId}
            className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-4 rounded-lg font-bold text-lg hover:from-teal-700 hover:to-emerald-700 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap flex items-center justify-center gap-2 shadow-lg"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Processing...
              </>
            ) : !providerId ? (
              <>
                <i className="ri-error-warning-line"></i>
                Please complete previous step
              </>
            ) : (
              <>
                Apply now — approval required
              </>
            )}
          </button>

          {/* Under-button Reassurance */}
          <p className="text-center text-sm text-gray-600 mt-4 px-4">
            You'll see your city tier and typical pay rates during your application, before payment.
          </p>

          {/* Footer Micro-Trust */}
          <div className="text-center mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              No contracts · Cancel anytime · Fair platform rules
            </p>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="mt-6">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back
        </button>
      </div>
    </div>
  );
}